import {
  COOKIE_CONSENT_UPDATED_EVENT,
  getCookieConsent,
  hasAnalyticsConsent,
  hasMarketingConsent,
} from "./cookieConsent";

const GTM_ID = process.env.REACT_APP_GTM_ID;
const GTM_SCRIPT_ID = "missionmovement-gtm";

let gtmInitialized = false;

const hasTrackingConsent = () =>
  hasAnalyticsConsent() || hasMarketingConsent();

const getConsentState = () => {
  const consent = getCookieConsent();

  return {
    analyticsConsent: consent?.analytics === true,
    marketingConsent: consent?.marketing === true,
  };
};

const canUseBrowser = () =>
  typeof window !== "undefined" && typeof document !== "undefined";

const getDataLayer = () => {
  if (!canUseBrowser()) return null;

  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

export const pushConsentState = () => {
  if (!GTM_ID || !canUseBrowser() || !window.dataLayer) return;

  window.dataLayer.push({
    event: "cookie_consent_update",
    ...getConsentState(),
  });
};

export const initGTM = () => {
  if (!GTM_ID || !canUseBrowser() || !hasTrackingConsent()) return false;

  const dataLayer = getDataLayer();
  if (!dataLayer) return false;

  if (gtmInitialized || document.getElementById(GTM_SCRIPT_ID)) {
    gtmInitialized = true;
    pushConsentState();
    return true;
  }

  pushConsentState();
  dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });

  const script = document.createElement("script");
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
    GTM_ID,
  )}`;
  document.head.appendChild(script);

  gtmInitialized = true;
  return true;
};

export const handleConsentUpdated = () => {
  if (hasTrackingConsent()) {
    initGTM();
    return;
  }

  pushConsentState();
};

export const bindConsentTrackingUpdates = () => {
  if (!canUseBrowser()) return () => {};

  window.addEventListener(COOKIE_CONSENT_UPDATED_EVENT, handleConsentUpdated);

  return () => {
    window.removeEventListener(
      COOKIE_CONSENT_UPDATED_EVENT,
      handleConsentUpdated,
    );
  };
};

export const trackEvent = (eventName, payload = {}) => {
  if (!GTM_ID || !canUseBrowser() || !hasTrackingConsent()) return;

  if (!initGTM() || !window.dataLayer) return;

  window.dataLayer.push({
    event: eventName,
    ...payload,
  });
};

export const trackPageView = (path) => {
  if (!canUseBrowser()) return;

  trackEvent("page_view", {
    page_path: path || window.location.pathname,
    page_location: window.location.href,
    page_title: document.title,
  });
};

export const trackPreparationCheckView = () => {
  trackEvent("prep_check_view", {
    source: "preparation-check",
  });
};

export const trackPreparationCheckLead = (payload = {}) => {
  trackEvent("prep_check_lead", {
    source: "preparation-check",
    ...payload,
  });
};

export const trackCheckoutStart = (payload = {}) => {
  trackEvent("checkout_start", payload);
};

export const trackContactClick = (payload = {}) => {
  trackEvent("contact_click", payload);
};

export const trackFieldEventInterest = (payload = {}) => {
  trackEvent("field_event_interest", payload);
};
